import React, { Component } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import classNames from 'classnames';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import ColumnHeader from './ColumnHeader';
import MultiFilterColumnHeader from './MultiFilterColumnHeader';

const messages = defineMessages({
  NoDataFound: 'No data found',
});

export default class ScrollableTable extends Component {
  defaultSearchParams = {
    page: 0,
    pageSize: 50,
    desc: false,
    filter: {},
    search: {},
    select: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      hasMoreData: false,
      scrollKey: 0,
      lastRequest: 0, // Request number
      loading: false,
      reloading: false,
      searchParams: {
        ...this.defaultSearchParams,
        ...props.initialSearchParams,
      },
    };
  }

  componentDidMount() {
    const { searchParams } = this.state;
    const { fetchOnMount, items, initialSearchParams } = this.props;
    this.scrollToTop();

    if (fetchOnMount) {
      this.loadMoreItems(searchParams.page);
    } else if (items.length === initialSearchParams.pageSize) {
      this.setState(state => ({ ...state, hasMoreData: true }));
    }
  }

  componentDidUpdate(prevProps) {
    const { initialSearchParams, items } = this.props;
    const { searchParams } = this.state;
    if (JSON.stringify(prevProps.initialSearchParams) !== JSON.stringify(initialSearchParams)) {
      const alteredSearchParams = {
        ...searchParams,
        ...initialSearchParams,
      };
      this.scrollToTop();
      this.retrieveItems(alteredSearchParams);
    }

    const count = items?.length ?? 0;
    const prevCount = prevProps?.items?.length ?? 0;
    if (count > 0 && count !== prevCount && count % initialSearchParams.pageSize === 0) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasMoreData: true });
    }
  }
  scrollToTop = () => {
    const { useWindow, scrollParentRef } = this.props;
    if (useWindow) {
      window.scrollTo(0, 0);
    } else if (scrollParentRef) {
      scrollParentRef.scrollTop = 0;
    }
  };

  retrieveItems = params => {
    const { loadItems } = this.props;
    const { lastRequest: previousRequest } = this.state;
    const promise = new Promise((resolve, reject) => {
      const currentRequest = previousRequest + 1;

      this.setState(
        state => {
          if (params.page === 0) {
            // First page request
            return {
              loading: true,
              reloading: true,
              lastRequest: currentRequest,
              // If the page was set to 0 and sthe current page is not 0, the key defined on the infinite scroller component has to be changed
              // for it to re-render and
              // https://github.com/CassetteRocks/react-infinite-scroller/issues/12
              scrollKey: state.scrollKey + 1,
              searchParams: {
                ...params,
              },
            };
          }

          // Next page request
          return {
            loading: true,
            reloading: false,
            lastRequest: currentRequest,
          };
        },
        () => {
          Promise.resolve(loadItems(params, currentRequest)).then(
            result => {
              const { lastRequest } = this.state;
              if (typeof result === 'undefined' || lastRequest !== currentRequest) {
                // Don't do anything if the request is cancelled or if this request is not the last
                resolve();
                return;
              }
              this.setState(
                state => ({
                  ...state,
                  loading: false,
                  reloading: false,
                  hasMoreData: !!result && result.length === params.pageSize,
                  searchParams: {
                    ...params,
                  },
                }),
                () => {
                  resolve(result);
                  const root = document.getElementById('main-container');
                  if (
                    !!result &&
                    result.length === params.pageSize &&
                    !!root &&
                    root.scrollHeight < window.innerHeight
                  ) {
                    this.loadMoreItems(params.page + 1);
                  }
                },
              );
            },
            error => {
              reject(error);
            },
          );
        },
      );
    });
    return promise;
  };

  loadMoreItems(page) {
    const { searchParams } = this.state;

    const newSearchParams = {
      ...searchParams,
      page,
    };
    this.retrieveItems(newSearchParams);
  }

  sort(newSortKey, descFirst = false) {
    this.setState(
      ({ searchParams }) => {
        const { sortKey, desc } = searchParams;

        return {
          searchParams: {
            ...searchParams,
            sortKey: newSortKey,
            // A new sort key restarts from the column's preferred direction; the same key toggles.
            desc: sortKey === newSortKey ? !desc : descFirst,
            page: 0,
          },
        };
      },
      () => {
        const { searchParams } = this.state;

        this.retrieveItems(searchParams).then(() => this.scrollToTop());
      },
    );
  }

  applyFilterChange(searchParams, property, filterRequest) {
    let { filter = {}, search = {}, select = {} } = searchParams;

    if (filterRequest.items) {
      if (filterRequest.items.length > 0) {
        filter = { ...filter, [property]: { items: filterRequest.items } };
      } else {
        const newFilter = { ...filter };
        delete newFilter[property];
        filter = newFilter;
      }
    }

    if (filterRequest.from !== undefined || filterRequest.until !== undefined) {
      if (filterRequest.from != null || filterRequest.until != null) {
        filter = {
          ...filter,
          [property]: { from: filterRequest.from, until: filterRequest.until },
        };
      } else {
        const newFilter = { ...filter };
        delete newFilter[property];
        filter = newFilter;
      }
    }

    if (filterRequest.lesser !== undefined || filterRequest.greater !== undefined) {
      if (filterRequest.lesser != null || filterRequest.greater != null) {
        filter = {
          ...filter,
          [property]: { lesser: filterRequest.lesser, greater: filterRequest.greater },
        };
      } else {
        const newFilter = { ...filter };
        delete newFilter[property];
        filter = newFilter;
      }
    }

    // No falsy because it can be an empty string
    if (filterRequest.searchValue !== null && filterRequest.searchValue !== undefined) {
      if (filterRequest.searchValue === '' && search[property]) {
        const { [property]: _ignore, ...otherSearch } = search;
        search = otherSearch;
      } else {
        search = { ...search, [property]: filterRequest.searchValue };
      }
    }

    if (filterRequest.selectedItem) {
      select = { ...select, [property]: filterRequest.selectedItem };
    } else {
      const { [property]: _ignore, ...otherSelect } = select;
      select = otherSelect;
    }

    return { filter, search, select };
  }

  filter(property) {
    return filterRequest => {
      const { searchParams = {} } = this.state;
      const { filter, search, select } = this.applyFilterChange(
        searchParams,
        property,
        filterRequest,
      );

      const alteredSearchParams = {
        ...searchParams,
        filter,
        search,
        select,
        page: 0,
      };
      this.setState(
        {
          searchParams: {
            ...searchParams,
            filter,
            search,
            select,
          },
        },
        () => {
          this.retrieveItems(alteredSearchParams).then(() => {
            this.scrollToTop();
            document.body.click(); // https://stackoverflow.com/a/47636953
          });
        },
      );
    };
  }

  filterMultiple(changes) {
    const { searchParams = {} } = this.state;

    let currentParams = { ...searchParams };
    for (const { property, filterRequest } of changes) {
      const { filter, search, select } = this.applyFilterChange(
        currentParams,
        property,
        filterRequest,
      );
      currentParams = { ...currentParams, filter, search, select };
    }

    const alteredSearchParams = {
      ...currentParams,
      page: 0,
    };
    this.setState(
      {
        searchParams: currentParams,
      },
      () => {
        this.retrieveItems(alteredSearchParams).then(() => {
          this.scrollToTop();
          document.body.click(); // https://stackoverflow.com/a/47636953
        });
      },
    );
  }

  render() {
    const { hasMoreData, searchParams, scrollKey, loading, reloading } = this.state;
    const {
      children,
      items,
      noDataLabel,
      renderRow,
      tableClassName,
      useWindow,
      renderTBody,
      tbodyStyle,
      scrollParentRef,
      noHover,
    } = this.props;

    const noDataFound = !items || items.length <= 0;
    const columns = [].concat(...children);
    const numberOfColumns = columns.length;

    return (
      <InfiniteScroll
        pageStart={searchParams.page}
        loadMore={() => this.loadMoreItems(searchParams.page + 1)}
        hasMore={hasMoreData && !loading}
        initialLoad={false}
        useWindow={useWindow}
        getScrollParent={() => scrollParentRef}
        key={scrollKey}
        // Sometimes the scroll component does not render the loading animation, e.g. when changing filters.
        // Therefore, when we set the loading state variable, render the loading animation ourselves and set the loader prop to something invisible
        loader={null}
      >
        <table
          className={classNames(
            'scrollable-table',
            { 'table table-hover': !noHover },
            tableClassName,
          )}
        >
          <thead>
            <tr>
              {columns.map(
                (column, idx) =>
                  column &&
                  (column.props.filters && column.props.filters.length > 0 ? (
                    <MultiFilterColumnHeader
                      {...column.props}
                      key={`header-multi-${column.props.title}-${idx}`}
                      onSort={
                        column.props.sortKey &&
                        ((sortKey, descFirst) => this.sort(sortKey, descFirst))
                      }
                      onFilterMultiple={changes => this.filterMultiple(changes)}
                      searchParams={searchParams}
                    />
                  ) : (
                    <ColumnHeader
                      {...column.props}
                      key={`header${column.props.title}${idx + 1}`}
                      onSort={
                        column.props.sortKey &&
                        ((sortKey, descFirst) => this.sort(sortKey, descFirst))
                      }
                      onFilter={
                        column.props.filter
                          ? value => this.filter(column.props.filter.key)(value)
                          : undefined
                      }
                      searchParams={searchParams}
                    />
                  )),
              )}
            </tr>
          </thead>

          {!loading && noDataFound && (
            <tbody>
              <tr className="no-data-row">
                <td colSpan={numberOfColumns}>{noDataLabel}</td>
              </tr>
            </tbody>
          )}

          {!reloading &&
            !!items &&
            !!items.length &&
            (renderTBody ? (
              <tbody style={tbodyStyle}>{items.map(item => renderRow(item))}</tbody>
            ) : (
              items.map(item => renderRow(item))
            ))}
        </table>
        {loading && (
          <div>
            <LoadingAnimation />
          </div>
        )}
      </InfiniteScroll>
    );
  }

  static propTypes = {
    /** Function that is called when new items should be retrieved. Takes search params in as parameter and should return the transformed data */
    loadItems: PropTypes.func.isRequired,
    /** Can be set to false, when the scroll component is used in a modal */
    useWindow: PropTypes.bool,
    children: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          title: PropTypes.string,
          sortKey: PropTypes.string,
          filter: PropTypes.shape({}),
          className: PropTypes.string,
          render: PropTypes.func,
        }),
        PropTypes.array,
        PropTypes.bool,
      ]),
    ).isRequired,
    initialSearchParams: PropTypes.shape({
      page: PropTypes.number,
      pageSize: PropTypes.number,
      desc: PropTypes.bool,
      filter: PropTypes.shape({}),
      search: PropTypes.shape({}),
    }),
    items: PropTypes.arrayOf(PropTypes.shape).isRequired,
    noDataLabel: PropTypes.node,
    fetchOnMount: PropTypes.bool,
    noHover: PropTypes.bool,
    /** Scroll container element, used instead of the window when useWindow is false */
    scrollParentRef: PropTypes.instanceOf(Element),
  };

  static defaultProps = {
    noDataLabel: <FormattedMessage {...messages.NoDataFound} />,
    useWindow: true,
    scrollParentRef: undefined,
    initialSearchParams: {
      page: 0,
      pageSize: 50,
      desc: false,
      filter: {},
      search: {},
    },
    fetchOnMount: true,
    noHover: false,
  };
}
